import React, { useMemo } from 'react';

import { useGetFaces } from '@vlabs/api/artemy/client';
import type { OutputFace } from '@vlabs/api/artemy/model';
import { Carousel, FaceSample } from '@vlabs/uikit';
import type { WithDataTestid } from '@vlabs/uikit/types';

import { buildTestid } from '@vlabs/monokit/shared/lib/utils';

import { PersonCardPin } from '../person-card-pin';
import st from './PersonCardFaces.module.sass';

type TPersonCardSize = 'large' | 'small';

interface PersonCardFacesPoint3Props extends WithDataTestid {
  cardId: string;
  size: TPersonCardSize;
}

const displayName = 'PersonCardFacesPoint3';
const testid = buildTestid(displayName);

function PersonCardFacesPoint3({
  cardId,
  size,
  'data-testid': dataTestId = undefined,
}: PersonCardFacesPoint3Props): React.JSX.Element {
  const getFaces = useGetFaces({ card_ids: [cardId] });

  const items: React.JSX.Element[] | undefined = useMemo(() => {
    if (getFaces.status !== 'success') return [];
    const faces = (getFaces.data.faces ?? []) as (OutputFace & { is_card_face?: boolean })[];
    const cardFaceIndex = faces.findIndex((f) => f.is_card_face === true);
    if (cardFaceIndex > -1) {
      const face = faces.splice(cardFaceIndex, 1)[0];
      faces.unshift(face);
    }
    return faces.map((face) => (
      <div
        className={st.PhotoContainer}
        key={face.face_id}
        {...testid(dataTestId)}
      >
        {!!face.is_card_face && size === 'large' && <PersonCardPin />}
        <FaceSample
          className={st.Sample}
          key={face.face_id}
          size={size}
          src={face.avatar ? `/api/${face.avatar}` : undefined}
          {...testid(face.face_id)}
        />
      </div>
    ));
  }, [getFaces.data, getFaces.status, size, dataTestId]);

  if (!items) {
    return (
      <FaceSample
        className={st.PhotoContainer}
        key="empty"
        size={size}
        {...testid(dataTestId, 'empty')}
      />
    );
  }

  if (items.length === 1) {
    return <React.Fragment>{items[0]}</React.Fragment>;
  }

  return (
    <Carousel
      className={st.Carousel}
      items={items}
      {...testid(dataTestId)}
    />
  );
}

export { PersonCardFacesPoint3 };
